// Simple sound manager to play sound effects
class SoundManager {
  private audioContext: AudioContext | null = null;
  private sounds: Map<string, AudioBuffer> = new Map();
  private isMuted: boolean = false;

  constructor() {
    // We'll initialize AudioContext on first sound play attempt
  }

  private initAudioContext() {
    if (!this.audioContext) {
      try {
        this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      } catch (error) {
        console.error('Web Audio API is not supported in this browser', error);
      }
    }
    return this.audioContext;
  }

  // Toggle mute state
  toggleMute() {
    this.isMuted = !this.isMuted;
    return this.isMuted;
  }

  // Create a shimmery sound with multiple overlapping tones like particles or coins
  createBloopSound() {
    if (!this.audioContext) this.initAudioContext();
    if (!this.audioContext || this.isMuted) return;

    // We'll create multiple oscillators with different frequencies and timings
    // to create a particle/shimmer effect like coins or chimes
    
    // Define our base frequencies for a pleasant chime-like sound
    const frequencies = [
      330, // E4
      392, // G4
      440, // A4
      523  // C5
    ];
    
    // Create a master gain to control overall volume
    const masterGain = this.audioContext.createGain();
    masterGain.gain.value = 0.2; // Increased from 0.07 to make it louder
    masterGain.connect(this.audioContext.destination);
    
    // Add a more pronounced reverb-like effect using delay
    const delay = this.audioContext.createDelay();
    delay.delayTime.value = 0.1; // Slightly longer delay for more presence
    const delayGain = this.audioContext.createGain();
    delayGain.gain.value = 0.35; // Increased from 0.2 for more fullness
    
    delay.connect(delayGain);
    delayGain.connect(delay); // Feedback loop
    delayGain.connect(masterGain);
    
    // Create multiple oscillators with slightly randomized parameters
    for (let i = 0; i < 12; i++) { // Increased from 8 to 12 for more particles and fuller sound
      // Create new oscillator and gain for this particle
      const osc = this.audioContext.createOscillator();
      const gain = this.audioContext.createGain();
      
      // Randomize oscillator type for variety
      const oscTypes = ['sine', 'triangle'] as OscillatorType[];
      osc.type = oscTypes[Math.floor(Math.random() * oscTypes.length)];
      
      // Pick a random base frequency from our scale
      const baseFreq = frequencies[Math.floor(Math.random() * frequencies.length)];
      
      // Add slight randomization to frequency (+/- 5 Hz) for a more natural sound
      const freqOffset = (Math.random() * 10) - 5;
      const freq = baseFreq + freqOffset;
      
      // Randomize the start time (between 0 and 0.15 seconds)
      const startTime = this.audioContext.currentTime + (Math.random() * 0.15);
      
      // Set a random duration between 0.1 and 0.3 seconds
      const duration = 0.1 + (Math.random() * 0.2);
      
      // Set the frequency
      osc.frequency.setValueAtTime(freq, startTime);
      
      // Some particles will slide up, others will slide down in pitch
      if (Math.random() > 0.5) {
        osc.frequency.exponentialRampToValueAtTime(
          freq * 1.2, // Slide up by 20%
          startTime + duration
        );
      } else {
        osc.frequency.exponentialRampToValueAtTime(
          freq * 0.8, // Slide down by 20%
          startTime + duration
        );
      }
      
      // Configure gain envelope with a quick attack and decay
      gain.gain.setValueAtTime(0.001, startTime);
      gain.gain.exponentialRampToValueAtTime(
        0.15 + (Math.random() * 0.1), // Increased from 0.05-0.1 to 0.15-0.25 for louder sound
        startTime + 0.01 // Quick attack
      );
      gain.gain.exponentialRampToValueAtTime(
        0.001,
        startTime + duration // Gentle decay
      );
      
      // Connect to both the master output and the delay for shimmer
      osc.connect(gain);
      gain.connect(masterGain);
      gain.connect(delay);
      
      // Schedule start and stop
      osc.start(startTime);
      osc.stop(startTime + duration + 0.1); // Add a little buffer time
    }
  }

  // Play a "bloop" sound with a popping landing for bubble up plus particle effects
  playBubbleUpSound() {
    if (this.isMuted || !this.audioContext) return;
    
    // Create main oscillator for the "bloop" part
    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();
    
    // Create a second oscillator for the "pop" landing sound
    const popOsc = this.audioContext.createOscillator();
    const popGain = this.audioContext.createGain();
    
    // Add effects for more interesting sound
    const filter = this.audioContext.createBiquadFilter();
    filter.type = 'lowpass'; // Changed to lowpass to remove high frequencies
    filter.frequency.value = 600; // Lower frequency cutoff
    filter.Q.value = 3; // Lower Q for less resonance/harshness
    
    // Main bloop sound - softer rising pitch
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(250, this.audioContext.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(
      450, // Much lower end frequency
      this.audioContext.currentTime + 0.35 // Same duration
    );
    
    // Configure main gain envelope
    gainNode.gain.setValueAtTime(0.001, this.audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(
      0.15, // Full volume for swap sound
      this.audioContext.currentTime + 0.05
    );
    // Quick fade out as the pop comes in
    gainNode.gain.exponentialRampToValueAtTime(
      0.001, 
      this.audioContext.currentTime + 0.35
    );
    
    // Configure the "pop" sound to happen right at the end - much lower pitch
    popOsc.type = 'sine'; // Changed to sine for smoother sound
    popOsc.frequency.setValueAtTime(500, this.audioContext.currentTime + 0.34); // Much lower frequency
    popOsc.frequency.exponentialRampToValueAtTime(
      400, // Lower end frequency
      this.audioContext.currentTime + 0.4
    );
    
    // Configure the pop envelope - softer attack, quick decay
    popGain.gain.setValueAtTime(0.001, this.audioContext.currentTime);
    popGain.gain.setValueAtTime(0.001, this.audioContext.currentTime + 0.34); // Start silent
    popGain.gain.linearRampToValueAtTime(0.35, this.audioContext.currentTime + 0.35); // Softer attack
    popGain.gain.exponentialRampToValueAtTime(0.001, this.audioContext.currentTime + 0.4); // Quick decay
    
    // Add shimmery particle sounds when the node is swapped up
    // Similar to createBloopSound but with different timing
    
    // Define our base frequencies - using brighter notes for upward movement
    const frequencies = [
      293, // D4
      349, // F4
      392, // G4
      440  // A4
    ];
    
    // Create a master gain for particles
    const particleGain = this.audioContext.createGain();
    particleGain.gain.value = 0.05; // Quieter particles
    particleGain.connect(this.audioContext.destination);
    
    // Add a subtle reverb-like effect
    const delay = this.audioContext.createDelay();
    delay.delayTime.value = 0.08;
    const delayGain = this.audioContext.createGain();
    delayGain.gain.value = 0.2;
    
    delay.connect(delayGain);
    delayGain.connect(delay); // Feedback loop
    delayGain.connect(particleGain);
    
    // Create multiple oscillators with slightly randomized parameters
    // These will start slightly after the main sound to match the visual particles
    for (let i = 0; i < 7; i++) { // More particles for upward movement
      const osc = this.audioContext.createOscillator();
      const gain = this.audioContext.createGain();
      
      // Randomize oscillator type
      const oscTypes = ['sine', 'triangle'] as OscillatorType[];
      osc.type = oscTypes[Math.floor(Math.random() * oscTypes.length)];
      
      // Pick a random frequency
      const baseFreq = frequencies[Math.floor(Math.random() * frequencies.length)];
      const freqOffset = (Math.random() * 10) - 5;
      const freq = baseFreq + freqOffset;
      
      // Randomize start time - slightly delayed from main sound
      const startTime = this.audioContext.currentTime + 0.15 + (Math.random() * 0.15);
      const duration = 0.1 + (Math.random() * 0.2);
      
      // Set frequency with a slight upward drift for upward movement
      osc.frequency.setValueAtTime(freq, startTime);
      osc.frequency.exponentialRampToValueAtTime(
        freq * 1.1, // Upward drift for bubble up
        startTime + duration
      );
      
      // Configure gain envelope
      gain.gain.setValueAtTime(0.001, startTime);
      gain.gain.linearRampToValueAtTime(
        0.03 + (Math.random() * 0.03), // Quieter random volume
        startTime + 0.01
      );
      gain.gain.exponentialRampToValueAtTime(
        0.001,
        startTime + duration
      );
      
      // Connect to output
      osc.connect(gain);
      gain.connect(particleGain);
      gain.connect(delay);
      
      // Schedule start and stop
      osc.start(startTime);
      osc.stop(startTime + duration + 0.1);
    }
    
    // Connect main bloop sound
    oscillator.connect(filter);
    filter.connect(gainNode);
    gainNode.connect(this.audioContext.destination);
    
    // Connect pop sound
    popOsc.connect(popGain);
    popGain.connect(this.audioContext.destination);
    
    // Start and stop both sounds
    oscillator.start();
    popOsc.start();
    oscillator.stop(this.audioContext.currentTime + 0.35);
    popOsc.stop(this.audioContext.currentTime + 0.4);
  }

  // Play a "bloop" sound with a gentle plunking landing for bubble down plus particle effects
  playBubbleDownSound() {
    if (this.isMuted || !this.audioContext) return;
    
    // Create main oscillator for the "bloop" part
    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();
    
    // Create a second oscillator for the "plunk" landing sound
    const plunkOsc = this.audioContext.createOscillator();
    const plunkGain = this.audioContext.createGain();
    
    // Add effects for more interesting sound
    const filter = this.audioContext.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 450; // Lower frequency cutoff
    filter.Q.value = 3; // Lower Q for less resonance/harshness
    
    // Main bloop sound - gentle falling pitch
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(350, this.audioContext.currentTime); // Lower starting frequency
    oscillator.frequency.exponentialRampToValueAtTime(
      150, // Keep same low end frequency
      this.audioContext.currentTime + 0.35 // Same duration
    );
    
    // Configure main gain envelope
    gainNode.gain.setValueAtTime(0.001, this.audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(
      0.15, // Full volume for swap sound
      this.audioContext.currentTime + 0.05
    );
    // Quick fade out as the plunk comes in
    gainNode.gain.exponentialRampToValueAtTime(
      0.001, 
      this.audioContext.currentTime + 0.35
    );
    
    // Configure the "plunk" sound to happen right at the end
    plunkOsc.type = 'sine'; // Changed to sine for smoother sound
    plunkOsc.frequency.setValueAtTime(180, this.audioContext.currentTime + 0.34); // Lower frequency
    plunkOsc.frequency.exponentialRampToValueAtTime(
      100, 
      this.audioContext.currentTime + 0.42
    );
    
    // Configure the plunk envelope - softer attack, quick decay
    plunkGain.gain.setValueAtTime(0.001, this.audioContext.currentTime);
    plunkGain.gain.setValueAtTime(0.001, this.audioContext.currentTime + 0.34); // Start silent
    plunkGain.gain.linearRampToValueAtTime(0.35, this.audioContext.currentTime + 0.35); // Softer attack
    plunkGain.gain.exponentialRampToValueAtTime(0.001, this.audioContext.currentTime + 0.42); // Quick decay
    
    // Add shimmery particle sounds when the node is removed
    // Similar to createBloopSound but with lower frequencies
    
    // Define our base frequencies - using lower notes for removal
    const frequencies = [
      220, // A3
      261, // C4
      293, // D4
      329  // E4
    ];
    
    // Create a master gain for particles
    const particleGain = this.audioContext.createGain();
    particleGain.gain.value = 0.05; // Quieter particles
    particleGain.connect(this.audioContext.destination);
    
    // Add a subtle reverb-like effect
    const delay = this.audioContext.createDelay();
    delay.delayTime.value = 0.1;
    const delayGain = this.audioContext.createGain();
    delayGain.gain.value = 0.15;
    
    delay.connect(delayGain);
    delayGain.connect(delay); // Feedback loop
    delayGain.connect(particleGain);
    
    // Create multiple oscillators with slightly randomized parameters
    // These will start slightly after the main sound to match the visual particles
    for (let i = 0; i < 6; i++) { // Fewer particles for removal
      const osc = this.audioContext.createOscillator();
      const gain = this.audioContext.createGain();
      
      // Randomize oscillator type
      const oscTypes = ['sine', 'triangle'] as OscillatorType[];
      osc.type = oscTypes[Math.floor(Math.random() * oscTypes.length)];
      
      // Pick a random frequency
      const baseFreq = frequencies[Math.floor(Math.random() * frequencies.length)];
      const freqOffset = (Math.random() * 10) - 5;
      const freq = baseFreq + freqOffset;
      
      // Randomize start time - slightly delayed from main sound
      const startTime = this.audioContext.currentTime + 0.2 + (Math.random() * 0.15);
      const duration = 0.1 + (Math.random() * 0.15);
      
      // Set frequency with a slight drift
      osc.frequency.setValueAtTime(freq, startTime);
      osc.frequency.exponentialRampToValueAtTime(
        freq * (Math.random() > 0.5 ? 0.8 : 1.1), // Subtle random drift
        startTime + duration
      );
      
      // Configure gain envelope
      gain.gain.setValueAtTime(0.001, startTime);
      gain.gain.linearRampToValueAtTime(
        0.03 + (Math.random() * 0.03), // Quieter random volume
        startTime + 0.01
      );
      gain.gain.exponentialRampToValueAtTime(
        0.001,
        startTime + duration
      );
      
      // Connect to output
      osc.connect(gain);
      gain.connect(particleGain);
      gain.connect(delay);
      
      // Schedule start and stop
      osc.start(startTime);
      osc.stop(startTime + duration + 0.1);
    }
    
    // Connect main bloop sound
    oscillator.connect(filter);
    filter.connect(gainNode);
    gainNode.connect(this.audioContext.destination);
    
    // Connect plunk sound
    plunkOsc.connect(plunkGain);
    plunkGain.connect(this.audioContext.destination);
    
    // Start and stop both sounds
    oscillator.start();
    plunkOsc.start();
    oscillator.stop(this.audioContext.currentTime + 0.35);
    plunkOsc.stop(this.audioContext.currentTime + 0.42);
  }
}

// Create a singleton instance
const soundManager = new SoundManager();
export default soundManager;